import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

export type PosLayoutMode = 'Detail Card View' | 'Image Grid View' | 'Compact List';
export type PosShortcutAction =
  | 'showShortcuts'
  | 'focusSearch'
  | 'focusBarcode'
  | 'toggleFavorites'
  | 'openPay'
  | 'checkout'
  | 'closeModal'
  | 'holdCart'
  | 'toggleHeldCarts'
  | 'openExpense'
  | 'openReturn'
  | 'openTodaySales'
  | 'openPending'
  | 'removeCartLine'
  | 'increaseCartQty'
  | 'decreaseCartQty'
  | 'prevCartLine'
  | 'nextCartLine'
  | 'cyclePaymentMode'
  | 'paymentModeCash'
  | 'paymentModeBank'
  | 'paymentModeBoth'
  | 'completeSale'
  | 'confirmPayment';

export type PosShortcutBindings = Record<PosShortcutAction, string>;

export const DEFAULT_SHORTCUTS: PosShortcutBindings = {
  showShortcuts: 'Alt+k',
  focusSearch: 'Alt+s',
  focusBarcode: 'Alt+b',
  toggleFavorites: 'Alt+f',
  openPay: 'F8',
  checkout: 'F8',
  closeModal: 'Escape',
  holdCart: 'Alt+h',
  toggleHeldCarts: 'F4',
  openExpense: 'Alt+e',
  openReturn: 'Alt+r',
  openTodaySales: 'F2',
  openPending: 'F3',
  removeCartLine: 'Delete',
  increaseCartQty: '=',
  decreaseCartQty: '-',
  prevCartLine: 'ArrowUp',
  nextCartLine: 'ArrowDown',
  cyclePaymentMode: 'Alt+m',
  paymentModeCash: 'Alt+1',
  paymentModeBank: 'Alt+2',
  paymentModeBoth: 'Alt+3',
  completeSale: 'Control+Enter',
  confirmPayment: 'Control+Enter',
};

const SHORTCUTS_STORAGE_VERSION = 5;

interface PosUiStore {
  touchMode: boolean | null;
  layoutOverride: PosLayoutMode | null;
  showFavoritesStrip: boolean;
  shortcuts: PosShortcutBindings;
  setTouchMode: (v: boolean | null) => void;
  setLayoutOverride: (v: PosLayoutMode | null) => void;
  setShowFavoritesStrip: (v: boolean) => void;
  setShortcut: (action: PosShortcutAction, key: string) => void;
  resetShortcuts: () => void;
}

function migrateShortcuts(prev: Partial<PosShortcutBindings> | undefined): PosShortcutBindings {
  return { ...DEFAULT_SHORTCUTS, ...prev, openPay: 'F8', checkout: 'F8', toggleHeldCarts: prev?.toggleHeldCarts || 'F4' };
}

export const usePosUiStore = create<PosUiStore>()(
  persist(
    (set) => ({
      touchMode: null,
      layoutOverride: null,
      showFavoritesStrip: true,
      shortcuts: { ...DEFAULT_SHORTCUTS },
      setTouchMode: (touchMode) => set({ touchMode }),
      setLayoutOverride: (layoutOverride) => set({ layoutOverride }),
      setShowFavoritesStrip: (showFavoritesStrip) => set({ showFavoritesStrip }),
      setShortcut: (action, key) =>
        set((s) => ({ shortcuts: { ...s.shortcuts, [action]: key } })),
      resetShortcuts: () => set({ shortcuts: { ...DEFAULT_SHORTCUTS } }),
    }),
    {
      name: 'pos-ui-prefs',
      version: SHORTCUTS_STORAGE_VERSION,
      storage: createJSONStorage(() => localStorage),
      migrate: (persisted, version) => {
        const state = persisted as PosUiStore;
        if (version < SHORTCUTS_STORAGE_VERSION) {
          return { ...state, shortcuts: migrateShortcuts(state.shortcuts) };
        }
        return state;
      },
    },
  ),
);
